import React, { useEffect, useMemo, useRef, useState } from "react";

/* Wordly – responsive & mobile-safe */

const WORDS = [
  "APPLE","GRAPE","MANGO","LEMON","BERRY","PEACH","PLUMS","OLIVE","CHILI","ONION",
  "BREAD","CAKES","PASTA","PIZZA","STEAK","SALAD","FRIES","SUGAR","HONEY","WHEAT",
  "WATER","RIVER","OCEAN","LAKES","EARTH","STONE","METAL","WOODS","CLOUD","STARS",
  "NORTH","SOUTH","EASTS","WESTS","WORLD","HUMAN","HEART","BRAIN","MOUTH","TEETH",
  "CHAIR","TABLE","CLOTH","MUSIC","SONGS","DANCE","MOVIE","BOOKS","STORY","DRAMA",
  "LIGHT","DARKS","FIRES","SMOKE","GLASS","CLOCK","DOORS","HOUSE","ROOMS","GATES",
  "PLANT","TREES","GRASS","LEAFY","ROSES","LILAC","DAISY","BIRDS","HORSE","TIGER",
  "WOMAN","MENLY","CHILD","YOUTH","OLDER","SMILE","CRYED","LAUGH","DREAM","SLEEP",
  "BLACK","WHITE","GREEN","BROWN","REDLY","BLUES","GOLDS","SILVR","PINKS","ORANG",
  "HAPPY","SADLY","ANGRY","LOVES","HATES","FAITH","HOPEY","LUCKY","MAGIC","PEACE",
  "BUILD","MAKER","TOOLS","TRAIN","PLANE","CARGO","TRUCK","BIKES","ROADS","BRIDG",
  "CODES","ARRAY","LOOPS","STACK","CLASS","LOGIN","EMAIL","PRINT","QUERY","VALUE",
    "ALERT","ERROR","DEBUG","FETCH","CACHE","LOCAL","STATE","PROPS","HOOKS","REACT",
  "VUEJS","ANGUL","JAVAS","PYTHON","RUBY","SWIFT","GOOGS","JAVA","HTML5","CSS3",
  "GRAPH","CHART","TABLE","GRID","LISTS","ITEMS","LINKS","BUTTON","INPUT","FORM",
  "SEARCH","FILTER","SORTS","PAGIN","LOADS","SAVEY","EDIT","DELET","UPDATE",
  "LOGIN","LOGOU","SIGNUP","RESET","MANGO", "BERRY", "PIZZA", "BREAD", "SUGAR"               
];
const VALID = new Set([...WORDS]);

const COLORS = {
  correct: { background: "#22c55e", color: "white", border: "1px solid #16a34a" },
  present: { background: "#f59e0b", color: "white", border: "1px solid #d97706" },
  absent:  { background: "#cbd5e1", color: "#0f172a", border: "1px solid #94a3b8" },
};
const KEY_COLORS_PRIORITY = { correct: 3, present: 2, absent: 1 };
const KEY_H = 42;                 // 48 on desktop if you like
const KEY_RADIUS = Math.round(KEY_H * 0.45);
const KEY_FONT = Math.max(16, Math.round(KEY_H * 0.45));
const KEY_FONT_LABEL = Math.max(13, Math.round(KEY_H * 0.38));


function dailyIndex() {
  const d = new Date();
  const n = d.getFullYear()*10000 + (d.getMonth()+1)*100 + d.getDate();
  return n % WORDS.length;
}

function evaluateGuess(guess, answer) {
  const res = Array(5).fill("absent");
  const a = answer.split("");
  const g = guess.split("");

  for (let i = 0; i < 5; i++) {
    if (g[i] === a[i]) { res[i] = "correct"; a[i] = "*"; g[i] = "_"; }
  }
  for (let i = 0; i < 5; i++) {
    if (res[i] !== "correct") {
      const k = a.indexOf(guess[i]);
      if (k !== -1) { res[i] = "present"; a[k] = "*"; }
    }
  }
  return res;
}

/* measure a DOM element's width (for dynamic tile size) */
function useWidth(ref) {
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setW(e.contentRect.width);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

export default function Wordly() {
  // layout refs
  const boardWrapRef = useRef(null);
  const boardWidth = useWidth(boardWrapRef);

  // derive responsive sizes from available board width
  // 5 tiles + 4 gaps must fit. We compute a gap proportional to tile size.
  const { TILE, GAP, KEY_H, KEY_MIN, KEY_WIDE, mobile } = useMemo(() => {
    // choose a comfortable max tile for desktop
    const MAX_TILE = 56;
    const MIN_TILE = 36;
    const PADDING = 0; // grid has no horizontal padding
    // try to fit with an initial guess
    let tile = MAX_TILE;
    let gap = 8;

    if (boardWidth > 0) {
      // solve for tile so 5*tile + 4*gap <= boardWidth
      // we keep gap ~ tile * 0.12 (capped)
      const rawGap = Math.min(10, Math.max(6, Math.round(boardWidth * 0.012)));
      gap = rawGap;
      tile = Math.floor((boardWidth - 4 * gap - PADDING) / 5);
      tile = Math.max(MIN_TILE, Math.min(MAX_TILE, tile));
    }

    const isMobile = tile <= 44; // heuristic: when tile is small, we’re on a narrow device
    return {
      TILE: tile,
      GAP: gap,
      KEY_H: isMobile ? 42 : 48,
      KEY_MIN: isMobile ? 34 : 40,
      KEY_WIDE: isMobile ? 64 : 76,
      mobile: isMobile,
    };
  }, [boardWidth]);

  // game state
  const [answerIndex, setAnswerIndex] = useState(dailyIndex);
  const answer = useMemo(() => WORDS[answerIndex], [answerIndex]);

  const [guesses, setGuesses] = useState([]);
  const [marks, setMarks] = useState([]);
  const [current, setCurrent] = useState("");
  const [message, setMessage] = useState(null);

  const done = useMemo(
    () => marks.some(m => m.every(x => x === "correct")) || guesses.length === 6,
    [marks, guesses]
  );
  const win = useMemo(() => marks.some(m => m.every(x => x === "correct")), [marks]);

  const kbState = useMemo(() => {
    const map = {};
    for (let i = 0; i < guesses.length; i++) {
      const g = guesses[i], m = marks[i];
      for (let j = 0; j < 5; j++) {
        const ch = g[j], state = m[j], old = map[ch];
        if (!old || KEY_COLORS_PRIORITY[state] > KEY_COLORS_PRIORITY[old]) map[ch] = state;
      }
    }
    return map;
  }, [guesses, marks]);

  function flash(type, text) {
    setMessage({ type, text });
    if (type === "info" || type === "bad") setTimeout(() => setMessage(null), 1400);
  }

  function onChar(ch) {
    if (done) return;
    if (!/^[A-Za-z]$/.test(ch)) return;
    if (current.length < 5) setCurrent(s => s + ch.toUpperCase());
  }
  function onBackspace() { if (!done) setCurrent(s => s.slice(0, -1)); }
  function onEnter() {
    if (done) return;
    if (current.length !== 5) return flash("info", "Need 5 letters");
    if (!VALID.has(current)) return flash("bad", "Not in word list");
    const m = evaluateGuess(current, answer);
    setGuesses(g => [...g, current]);
    setMarks(mk => [...mk, m]);
    setCurrent("");
  }

  useEffect(() => {
    function handle(e) {
      const k = e.key;
      if (k === "Enter") { onEnter(); e.preventDefault(); }
      else if (k === "Backspace") { onBackspace(); e.preventDefault(); }
      else if (/^[a-zA-Z]$/.test(k)) { onChar(k); e.preventDefault(); }
    }
    window.addEventListener("keydown", handle);
    return () => window.removeEventListener("keydown", handle);
  });

  function newGameRandom() {
    const next = (answerIndex + Math.floor(1 + Math.random() * (WORDS.length - 1))) % WORDS.length;
    startWithIndex(next);
  }
  function startWithIndex(idx) {
    const i = ((idx % WORDS.length) + WORDS.length) % WORDS.length;
    setAnswerIndex(i); setGuesses([]); setMarks([]); setCurrent(""); setMessage(null);
  }
  function daily() { startWithIndex(dailyIndex()); }

  const keyboardRows = [
    "QWERTYUIOP".split(""),
    "ASDFGHJKL".split(""),
    ["ENTER", ..."ZXCVBNM".split(""), "⌫"],
  ];

  // inline style helpers (computed from dynamic sizes)
  const styles = {
    page: {
      minHeight: "100svh",             // safe viewport height (prevents iOS chrome jump)
      background: "#f8fafc",
      color: "#0f172a",
      padding: 12,
      fontFamily: "Inter, system-ui, Avenir, Helvetica, Arial, sans-serif",
      boxSizing: "border-box",
      display: "flex",
      justifyContent: "center",
    },
    container: { width: "100%", maxWidth: 720 },
    header: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      marginBottom: mobile ? 6 : 12, gap: 8, flexWrap: "wrap"
    },
    title: { fontSize: mobile ? 24 : 28, fontWeight: 800, margin: 0 },
    sub: { fontSize: mobile ? 12 : 13, color: "#475569" },
    btn: {
      padding: mobile ? "6px 10px" : "8px 12px",
      borderRadius: 12, border: "1px solid #e2e8f0", background: "white",
      cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.06)"
    },
    grid: {
      display: "grid",
      gridTemplateColumns: `repeat(5, ${TILE}px)`,
      gap: GAP,
      justifyContent: "center",
      margin: mobile ? "8px auto" : "16px auto",
    },
    tile: {
      width: TILE, height: TILE, borderRadius: 10,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.max(18, Math.round(TILE * 0.45)),
      fontWeight: 800, border: "1px solid #cbd5e1", background: "white"
    },
    tileFilled: { border: "1px solid #94a3b8" },
    banner: { marginTop: mobile ? 8 : 12, display: "flex", justifyContent: "center" },
    ok:  { padding: "10px 14px", borderRadius: 12, background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0", fontWeight: 600 },
    bad: { padding: "10px 14px", borderRadius: 12, background: "#fef2f2", color: "#991b1b", border: "1px solid #fecaca", fontWeight: 600 },
    info: { padding: "10px 14px", borderRadius: 12, background: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe", fontWeight: 600 },

    // sticky keyboard bar
    kbdWrap: {
  position: "sticky",
  bottom: 0,
  maxWidth: 640,
  margin: "10px auto 0",
  display: "grid",
  gap: 8, // or 6 on mobile if you prefer
  paddingTop: 6,
  paddingBottom: "calc(6px + env(safe-area-inset-bottom, 0px))",
  background: "linear-gradient(180deg, rgba(248,250,252,0) 0%, #f8fafc 18%)",
  backdropFilter: "saturate(120%)",
  zIndex: 1
},

// A row as a responsive grid. Pass the column count.
krowGrid: (cols) => ({
  display: "grid",
  gridTemplateColumns: `repeat(${cols}, 1fr)`,
  columnGap: 6,
  rowGap: 6,
  alignItems: "stretch",
}),
 keyGrid: {
    height: KEY_H,
    width: "100%",
    minWidth: 0,                 // allow shrinking in narrow phones
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: KEY_RADIUS,
    border: "1px solid #d7e0ea",
    background: "#fff",
    fontWeight: 800,
    fontSize: KEY_FONT,
    lineHeight: 1,
    padding: 0,                  // exact hit area = visual pill
    whiteSpace: "nowrap",        // never wrap “Enter”
    overflow: "hidden",
    userSelect: "none",
    touchAction: "manipulation",
    boxShadow: "0 1px 0 rgba(0,0,0,.03)",
  },
// Each key fills its grid cell; no fixed min-widths → never wraps
keyGrid: {
  width: "100%",
  minWidth: 0,             // allow shrinking inside grid cells
  height: 42,              // tweak for mobile; make 48 on desktop if you want
  padding: "0 6px",
  borderRadius: 10,
  border: "1px solid #e2e8f0",
  background: "white",
  fontWeight: 700,
  cursor: "pointer",
  lineHeight: 1,
},keyLabel: {
    fontSize: KEY_FONT_LABEL,
    fontWeight: 700,
    letterSpacing: 0.2,
  },
    krow: { display: "flex", gap: mobile ? 6 : 8, justifyContent: "center" },
    key: {
      height: KEY_H, minWidth: KEY_MIN, padding: "0 8px",
      borderRadius: 10, border: "1px solid #e2e8f0",
      background: "white", cursor: "pointer", fontWeight: 700
    },
    keyWide: { minWidth: KEY_WIDE },
  };

  function tileStyle(state, filled) {
    let base = { ...styles.tile };
    if (filled) base = { ...base, ...styles.tileFilled };
    if (!state) return base;
    if (state === "correct") return { ...base, ...COLORS.correct };
    if (state === "present") return { ...base, ...COLORS.present };
    return { ...base, ...COLORS.absent };
  }

  const rowsToShow = 6;
  const filledRows = guesses.length;
  const emptyRows = rowsToShow - filledRows - (done ? 0 : 1);
  const showCurrentRow = !done;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Wordly</h1>
            <div style={styles.sub}>Guess the 5-letter word in 6 tries.</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={styles.btn} onClick={daily}>Daily</button>
            <button style={styles.btn} onClick={newGameRandom}>New Game</button>
          </div>
        </div>

        {/* board wrapper measured by ResizeObserver */}
        <div ref={boardWrapRef}>
          {/* Completed rows */}
          {guesses.map((g, i) => (
            <div key={`row-${i}`} style={styles.grid}>
              {g.split("").map((ch, j) => (
                <div key={j} style={tileStyle(marks[i][j], true)}>{ch}</div>
              ))}
            </div>
          ))}

          {/* Current row */}
          {showCurrentRow && (
            <div style={styles.grid}>
              {Array.from({ length: 5 }).map((_, i) => {
                const ch = current[i] || "";
                return <div key={i} style={tileStyle(null, ch !== "")}>{ch}</div>;
              })}
            </div>
          )}

          {/* Remaining empty rows */}
          {Array.from({ length: Math.max(0, emptyRows) }).map((_, r) => (
            <div key={`empty-${r}`} style={styles.grid}>
              {Array.from({ length: 5 }).map((__, i) => (
                <div key={i} style={styles.tile}></div>
              ))}
            </div>
          ))}
        </div>

        {/* Banner */}
        <div style={styles.banner}>
          {message && (
            <div style={message.type === "ok" ? styles.ok : message.type === "bad" ? styles.bad : styles.info}>
              {message.text}
            </div>
          )}
          {!message && done && (
            <div style={win ? styles.ok : styles.bad}>
              {win ? "🎉 You solved it!" : `😅 The word was ${answer}.`}
            </div>
          )}
        </div>

        {/* Keyboard */}
<div style={styles.kbdWrap}>
  {/* row 1: 10 columns */}
  <div style={styles.krowGrid(10)}>
    {"QWERTYUIOP".split("").map(k => (
      <button
        key={k}
        style={styles.keyGrid}
        onClick={() => onChar(k)}
      >
        {k}
      </button>
    ))}
  </div>

  {/* row 2: 9 columns */}
  <div style={styles.krowGrid(9)}>
    {"ASDFGHJKL".split("").map(k => (
      <button
        key={k}
        style={styles.keyGrid}
        onClick={() => onChar(k)}
      >
        {k}
      </button>
    ))}
  </div>

  {/* row 3: 11 columns; Enter + ⌫ span 2 columns */}
  <div style={styles.krowGrid(11)}>
    <button
      style={{ ...styles.keyGrid, gridColumn: "span 2" }}
      onClick={onEnter}
    >
      Enter
    </button>

    {"ZXCVBNM".split("").map(k => (
      <button
        key={k}
        style={styles.keyGrid}
        onClick={() => onChar(k)}
      >
        {k}
      </button>
    ))}

    <button
      style={{ ...styles.keyGrid, gridColumn: "span 2" }}
      onClick={onBackspace}
    >
      ⌫
    </button>
  </div>
</div>
      </div>
    </div>
  );
}